import React, { useContext, useEffect, useState } from "react";
import {
  EditPopover,
  NumberInput,
  Panel,
  RadioButtonInput,
  RangeNumberInput,
  SelectInput,
  Tabs,
  TabsV2,
  TextInput,
  ToggleInput,
  UnitInput,
} from "../input";
import { GrigoraEditorContext } from "../editor/context";
import { isEmpty } from "lodash";
import GrigoraCSSRenderer from "./../blocks/css-render";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { changeTransition, isUpdatedFromDefault } from "../utils";
import { INPUT_LIMITS } from "../input-limits";
function CSSTransformAnimations({ block, actions, ...props }) {
  const {
    id,
    props: { transformAnimation = {}, transition },
    dom,
  } = block;

  const { setProp } = actions;

  const editorContext = useContext(GrigoraEditorContext);

  const [active, setActive] = useState("desktop");

  useEffect(() => {
    if (editorContext?.displaySize) {
      setActive(editorContext.displaySize);
    }
  }, [editorContext?.displaySize]);

  function changeAxis(state, axis, transform, val) {
    setProp((props) => {
      props.transformAnimation = {
        ...(transformAnimation ? transformAnimation : {}),
        [active]: {
          ...(transformAnimation?.[active] ? transformAnimation?.[active] : {}),
          [state]: {
            ...(transformAnimation?.[active]?.[state]
              ? transformAnimation?.[active]?.[state]
              : {}),
            [transform]: {
              ...(transformAnimation?.[active]?.[state]?.[transform]
                ? transformAnimation?.[active]?.[state]?.[transform]
                : {}),
              [axis]: val,
            },
          },
        },
      };
    }, 300);
  }

  function changeTransform(state, transform, val) {
    setProp((props) => {
      props.transformAnimation = {
        ...(transformAnimation ? transformAnimation : {}),
        [active]: {
          ...(transformAnimation?.[active] ? transformAnimation?.[active] : {}),
          [state]: {
            ...(transformAnimation?.[active]?.[state]
              ? transformAnimation?.[active]?.[state]
              : {}),
            [transform]: val,
          },
        },
      };
    }, 300);
  }

  function removeTransform(state, transform) {
    setProp((props) => {
      props.transformAnimation = {
        ...(transformAnimation ? transformAnimation : {}),
        [active]: {
          ...(transformAnimation?.[active] ? transformAnimation?.[active] : {}),
          [state]: {
            ...(transformAnimation?.[active]?.[state]
              ? transformAnimation?.[active]?.[state]
              : {}),
            [transform]: undefined,
          },
        },
      };
    }, 300);
  }

  function InitialSettings() {
    return (
      <>
        <EditPopover
          label={"Shift"}
          canReset={!isEmpty(transformAnimation?.[active]?.initial?.shift)}
          onReset={() => {
            removeTransform("initial", "shift");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.shift?.x ?? ""}
            onChange={(val) => {
              changeAxis("initial", "x", "shift", val);
            }}
            label={"X"}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.shift?.y ?? ""}
            onChange={(val) => {
              changeAxis("initial", "y", "shift", val);
            }}
            label={"Y"}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.shift?.z ?? ""}
            onChange={(val) => {
              changeAxis("initial", "z", "shift", val);
            }}
            label={"Z"}
          />
        </EditPopover>
        <EditPopover
          label={"Rotate"}
          canReset={!isEmpty(transformAnimation?.[active]?.initial?.rotate)}
          onReset={() => {
            removeTransform("initial", "rotate");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.rotate?.x ?? ""}
            onChange={(val) => {
              changeAxis("initial", "x", "rotate", val);
            }}
            label={"X"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.rotate?.y ?? ""}
            onChange={(val) => {
              changeAxis("initial", "y", "rotate", val);
            }}
            label={"Y"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.rotate?.z ?? ""}
            onChange={(val) => {
              changeAxis("initial", "z", "rotate", val);
            }}
            label={"Z"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
        </EditPopover>
        <EditPopover
          label={"Skew"}
          canReset={!isEmpty(transformAnimation?.[active]?.initial?.skew)}
          onReset={() => {
            removeTransform("initial", "skew");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.skew?.x ?? ""}
            onChange={(val) => {
              changeAxis("initial", "x", "skew", val);
            }}
            label={"X"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.initial?.skew?.y ?? ""}
            onChange={(val) => {
              changeAxis("initial", "y", "skew", val);
            }}
            label={"Y"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
        </EditPopover>
        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-initial-scale"].min}
          max={INPUT_LIMITS["css-transform-animations-initial-scale"].max}
          step={INPUT_LIMITS["css-transform-animations-initial-scale"].step}
          value={transformAnimation?.[active]?.initial?.scale}
          onChange={(val) => {
            changeTransform("initial", "scale", val);
          }}
          label={"Scale"}
        />
        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-initial-opacity"].min}
          max={INPUT_LIMITS["css-transform-animations-initial-opacity"].max}
          step={INPUT_LIMITS["css-transform-animations-initial-opacity"].step}
          value={transformAnimation?.[active]?.initial?.opacity}
          onChange={(val) => {
            changeTransform("initial", "opacity", val);
          }}
          label={"Opacity"}
        />
        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-initial-perspective"].min}
          max={INPUT_LIMITS["css-transform-animations-initial-perspective"].max}
          step={
            INPUT_LIMITS["css-transform-animations-initial-perspective"].step
          }
          value={transformAnimation?.[active]?.initial?.perspective}
          onChange={(val) => {
            changeTransform("initial", "perspective", val);
          }}
          label={"Perspective"}
        />

        <div className="text-xs">Transform Style</div>
        <ToggleInput
          value={
            transformAnimation?.[active]?.initial?.transformStyle?.preserve3d
          }
          onChange={(val) => {
            changeAxis("initial", "preserve3d", "transformStyle", val);
          }}
          label={"Preserve 3D"}
        />
        <ToggleInput
          value={
            transformAnimation?.[active]?.initial?.transformStyle
              ?.backfaceHidden
          }
          onChange={(val) => {
            changeAxis("initial", "backfaceHidden", "transformStyle", val);
          }}
          label={"Backface Hidden"}
        />
        <SelectInput
          resetValue={undefined}
          value={transformAnimation?.[active]?.initial?.origin}
          onChange={(val) => {
            changeTransform("initial", "origin", val);
          }}
          options={[
            { label: "-", value: "" },
            { label: "Top Left", value: "top left" },
            { label: "Top Center", value: "top center" },
            { label: "Top Right", value: "top right" },
            { label: "Center Left", value: "center left" },
            { label: "Center Center", value: "center center" },
            { label: "Center Right", value: "center right" },
            { label: "Bottom Left", value: "bottom left" },
            { label: "Bottom Center", value: "bottom center" },
            { label: "Bottom Right", value: "bottom right" },
          ]}
          label="Transform Origin"
        />
      </>
    );
  }

  function HoverSettings() {
    return (
      <>
        <TextInput
          minLength={INPUT_LIMITS["custom-classname"].minlength}
          maxLength={INPUT_LIMITS["custom-classname"].maxlength}
          isResetVisible={false}
          value={transformAnimation?.[active]?.hover?.parent ?? ""}
          onChange={(val) => {
            changeTransform("hover", "parent", val);
          }}
          placeholder=".class-name"
          label={"Custom Parent Hover Trigger"}
        />
        <EditPopover
          label={"Shift"}
          canReset={!isEmpty(transformAnimation?.[active]?.hover?.shift)}
          onReset={() => {
            removeTransform("hover", "shift");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.shift?.x ?? ""}
            onChange={(val) => {
              changeAxis("hover", "x", "shift", val);
            }}
            label={"X"}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.shift?.y ?? ""}
            onChange={(val) => {
              changeAxis("hover", "y", "shift", val);
            }}
            label={"Y"}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.shift?.z ?? ""}
            onChange={(val) => {
              changeAxis("hover", "z", "shift", val);
            }}
            label={"Z"}
          />
        </EditPopover>
        <EditPopover
          label={"Rotate"}
          canReset={!isEmpty(transformAnimation?.[active]?.hover?.rotate)}
          onReset={() => {
            removeTransform("hover", "rotate");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.rotate?.x ?? ""}
            onChange={(val) => {
              changeAxis("hover", "x", "rotate", val);
            }}
            label={"X"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.rotate?.y ?? ""}
            onChange={(val) => {
              changeAxis("hover", "y", "rotate", val);
            }}
            label={"Y"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.rotate?.z ?? ""}
            onChange={(val) => {
              changeAxis("hover", "z", "rotate", val);
            }}
            label={"Z"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
        </EditPopover>
        <EditPopover
          label={"Skew"}
          canReset={!isEmpty(transformAnimation?.[active]?.hover?.skew)}
          onReset={() => {
            removeTransform("hover", "skew");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.skew?.x ?? ""}
            onChange={(val) => {
              changeAxis("hover", "x", "skew", val);
            }}
            label={"X"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.hover?.skew?.y ?? ""}
            onChange={(val) => {
              changeAxis("hover", "y", "skew", val);
            }}
            label={"Y"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
        </EditPopover>
        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-hover-scale"].min}
          max={INPUT_LIMITS["css-transform-animations-hover-scale"].max}
          step={INPUT_LIMITS["css-transform-animations-hover-scale"].step}
          value={transformAnimation?.[active]?.hover?.scale}
          onChange={(val) => {
            changeTransform("hover", "scale", val);
          }}
          label={"Scale"}
        />
        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-hover-opacity"].min}
          max={INPUT_LIMITS["css-transform-animations-hover-opacity"].max}
          step={INPUT_LIMITS["css-transform-animations-hover-opacity"].step}
          value={transformAnimation?.[active]?.hover?.opacity}
          onChange={(val) => {
            changeTransform("hover", "opacity", val);
          }}
          label={"Opacity"}
        />
        <RangeNumberInput
          resetValue={undefined}
          min={0}
          max={10}
          step={0.01}
          value={transition?.types?.["transform"]?.duration}
          onChange={(val) => {
            changeTransition(
              ["transform"],
              { duration: val },
              transition,
              setProp,
            );
          }}
          label={"Duration"}
        />
        <RangeNumberInput
          resetValue={undefined}
          min={0}
          max={10}
          step={0.01}
          value={transition?.types?.["transform"]?.delay}
          onChange={(val) => {
            changeTransition(
              ["transform"],
              { delay: val },
              transition,
              setProp,
            );
          }}
          label={"Delay"}
        />
        <SelectInput
          resetValue={undefined}
          value={transition?.types?.["transform"]?.easing}
          onChange={(val) => {
            changeTransition(
              ["transform"],
              { easing: val },
              transition,
              setProp,
            );
          }}
          options={[
            { label: "Linear", value: "" },
            { label: "Ease", value: "ease" },
            { label: "Ease In", value: "ease-in" },
            { label: "Ease Out", value: "ease-out" },
            { label: "Ease In Out", value: "ease-in-out" },
          ]}
          label="Easing"
        />
      </>
    );
  }

  function InfiniteSettings() {
    return (
      <>
        <TextInput
          minLength={INPUT_LIMITS["custom-classname"].minlength}
          maxLength={INPUT_LIMITS["custom-classname"].maxlength}
          isResetVisible={false}
          value={transformAnimation?.[active]?.infinite?.parent ?? ""}
          onChange={(val) => {
            changeTransform("infinite", "parent", val);
          }}
          placeholder=".class-name"
          label={"Custom Parent Hover Trigger"}
        />
        <EditPopover
          label={"Shift"}
          canReset={!isEmpty(transformAnimation?.[active]?.infinite?.shift)}
          onReset={() => {
            removeTransform("infinite", "shift");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.shift?.x ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "x", "shift", val);
            }}
            label={"X"}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.shift?.y ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "y", "shift", val);
            }}
            label={"Y"}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.shift?.z ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "z", "shift", val);
            }}
            label={"Z"}
          />
        </EditPopover>

        <EditPopover
          label={"Rotate"}
          canReset={!isEmpty(transformAnimation?.[active]?.infinite?.rotate)}
          onReset={() => {
            removeTransform("infinite", "rotate");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.rotate?.x ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "x", "rotate", val);
            }}
            label={"X"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.rotate?.y ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "y", "rotate", val);
            }}
            label={"Y"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.rotate?.z ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "z", "rotate", val);
            }}
            label={"Z"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
        </EditPopover>

        <EditPopover
          label={"Skew"}
          canReset={!isEmpty(transformAnimation?.[active]?.infinite?.skew)}
          onReset={() => {
            removeTransform("infinite", "skew");
          }}
        >
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.skew?.x ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "x", "skew", val);
            }}
            label={"X"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
          <UnitInput
            isNegativeValid
            resetValue={""}
            value={transformAnimation?.[active]?.infinite?.skew?.y ?? ""}
            onChange={(val) => {
              changeAxis("infinite", "y", "skew", val);
            }}
            label={"Y"}
            units={[
              {
                label: "deg",
                value: "deg",
              },
            ]}
            unit={{
              label: "deg",
              value: "deg",
            }}
          />
        </EditPopover>

        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-infinite-scale"].min}
          max={INPUT_LIMITS["css-transform-animations-infinite-scale"].max}
          step={INPUT_LIMITS["css-transform-animations-infinite-scale"].step}
          value={transformAnimation?.[active]?.infinite?.scale}
          onChange={(val) => {
            changeTransform("infinite", "scale", val);
          }}
          label={"Scale"}
        />
        <NumberInput
          resetValue={undefined}
          min={INPUT_LIMITS["css-transform-animations-infinite-opacity"].min}
          max={INPUT_LIMITS["css-transform-animations-infinite-opacity"].max}
          step={INPUT_LIMITS["css-transform-animations-infinite-opacity"].step}
          value={transformAnimation?.[active]?.infinite?.opacity}
          onChange={(val) => {
            changeTransform("infinite", "opacity", val);
          }}
          label={"Opacity"}
        />
        <RangeNumberInput
          resetValue={undefined}
          min={0}
          max={10}
          step={0.01}
          value={transformAnimation?.[active]?.infinite?.duration}
          onChange={(val) => {
            changeTransform("infinite", "duration", val);
          }}
          label={"Duration"}
        />
        <RangeNumberInput
          resetValue={undefined}
          min={0}
          max={10}
          step={0.01}
          value={transformAnimation?.[active]?.infinite?.delay}
          onChange={(val) => {
            changeTransform("infinite", "delay", val);
          }}
          label={"Delay"}
        />
        <SelectInput
          resetValue={undefined}
          value={transformAnimation?.[active]?.infinite?.direction}
          onChange={(val) => {
            changeTransform("infinite", "direction", val);
          }}
          options={[
            { label: "Normal", value: "" },
            { label: "Reverse", value: "reverse" },
            { label: "Alternate", value: "alternate" },
          ]}
          label="Direction"
        />
        <SelectInput
          resetValue={undefined}
          value={transformAnimation?.[active]?.infinite?.easing}
          onChange={(val) => {
            changeTransform("infinite", "easing", val);
          }}
          options={[
            { label: "Linear", value: "" },
            { label: "Ease", value: "ease" },
            { label: "Ease In", value: "ease-in" },
            { label: "Ease Out", value: "ease-out" },
            { label: "Ease In Out", value: "ease-in-out" },
          ]}
          label="Easing"
        />
      </>
    );
  }

  const tabs = [
    {
      label: "Initial",
      key: "initial",
      children: InitialSettings(),
    },
    {
      label: "Hover",
      key: "hover",
      children: HoverSettings(),
    },
    {
      label: "Infinite",
      key: "infinite",
      children: InfiniteSettings(),
    },
  ];

  return (
    <>
      <Panel
        isUpdated={isUpdatedFromDefault([
          { key: transformAnimation, default: {} },
        ])}
        id={`block-setting-${id}-css-transform-animations`}
        title={"CSS Transform Animations"}
        initialOpen={false}
      >
        {!(transformAnimation && isEmpty(transformAnimation)) && (
          <>
            <div className="flex text-xs justify-between mb-2">
              Reset Animations
              <div
                onClick={() => {
                  setProp((props) => {
                    props.transformAnimation = undefined;
                  });
                }}
                className="mr-2 cursor-pointer"
              >
                <FontAwesomeIcon icon={faRefresh} size="sm" />
              </div>
            </div>
          </>
        )}

        <TabsV2 items={tabs} />
      </Panel>
    </>
  );
}

function transformProps(props, supportProps) {
  if (props.transformAnimation && !isEmpty(props.transformAnimation)) {
    supportProps = {
      ...supportProps,
      className: {
        ...(supportProps.className ? supportProps.className : {}),
        [`transform-${props.id}`]: true,
      },
    };
  }
  return supportProps;
}

function dynamicCSS(id, props) {
  const { transformAnimation } = props;

  if (!(props.transformAnimation && !isEmpty(props.transformAnimation))) {
    return ``;
  }

  return `
  .transform-${id} {
    ${GrigoraCSSRenderer.transform([
      {
        wrapper: "perspective",
        value: transformAnimation?.desktop?.initial?.perspective
          ? transformAnimation?.desktop?.initial?.perspective + "px"
          : undefined,
      },
      {
        wrapper: "translateX",
        value: transformAnimation?.desktop?.initial?.shift?.x,
      },
      {
        wrapper: "translateY",
        value: transformAnimation?.desktop?.initial?.shift?.y,
      },
      {
        wrapper: "translateZ",
        value: transformAnimation?.desktop?.initial?.shift?.z,
      },
      {
        wrapper: "rotateX",
        value: transformAnimation?.desktop?.initial?.rotate?.x,
      },
      {
        wrapper: "rotateY",
        value: transformAnimation?.desktop?.initial?.rotate?.y,
      },
      {
        wrapper: "rotateZ",
        value: transformAnimation?.desktop?.initial?.rotate?.z,
      },
      {
        wrapper: "skewX",
        value: transformAnimation?.desktop?.initial?.skew?.x,
      },
      {
        wrapper: "skewY",
        value: transformAnimation?.desktop?.initial?.skew?.y,
      },
      {
        wrapper: "scale",
        value: transformAnimation?.desktop?.initial?.scale,
      },
    ])}
    ${GrigoraCSSRenderer.opacity(transformAnimation?.desktop?.initial?.opacity)}
    ${GrigoraCSSRenderer.transformOrigin(
      transformAnimation?.desktop?.initial?.origin,
    )}
    ${GrigoraCSSRenderer.transformStyle(
      transformAnimation?.desktop?.initial?.transformStyle?.preserve3d
        ? "preserve-3d"
        : undefined,
    )}
    ${GrigoraCSSRenderer.backfaceVisibility(
      transformAnimation?.desktop?.initial?.transformStyle?.backfaceHidden
        ? "hidden"
        : undefined,
    )}
  }

  ${
    transformAnimation?.desktop?.hover?.parent
      ? `${transformAnimation?.desktop?.hover?.parent}:hover .transform-${id}`
      : `.transform-${id}:hover`
  } {
    ${GrigoraCSSRenderer.transform([
      {
        wrapper: "perspective",
        value: transformAnimation?.desktop?.hover?.perspective
          ? transformAnimation?.desktop?.hover?.perspective + "px"
          : undefined,
      },
      {
        wrapper: "translateX",
        value: transformAnimation?.desktop?.hover?.shift?.x,
      },
      {
        wrapper: "translateY",
        value: transformAnimation?.desktop?.hover?.shift?.y,
      },
      {
        wrapper: "translateZ",
        value: transformAnimation?.desktop?.hover?.shift?.z,
      },
      {
        wrapper: "rotateX",
        value: transformAnimation?.desktop?.hover?.rotate?.x,
      },
      {
        wrapper: "rotateY",
        value: transformAnimation?.desktop?.hover?.rotate?.y,
      },
      {
        wrapper: "rotateZ",
        value: transformAnimation?.desktop?.hover?.rotate?.z,
      },
      {
        wrapper: "skewX",
        value: transformAnimation?.desktop?.hover?.skew?.x,
      },
      {
        wrapper: "skewY",
        value: transformAnimation?.desktop?.hover?.skew?.y,
      },
      { wrapper: "scale", value: transformAnimation?.desktop?.hover?.scale },
    ])}
    ${GrigoraCSSRenderer.opacity(transformAnimation?.desktop?.hover?.opacity)}
  }

  @keyframes transform-${id} {
    100% {
      ${GrigoraCSSRenderer.transform([
        {
          wrapper: "perspective",
          value: transformAnimation?.desktop?.infinite?.perspective
            ? transformAnimation?.desktop?.infinite?.perspective + "px"
            : undefined,
        },
        {
          wrapper: "translateX",
          value: transformAnimation?.desktop?.infinite?.shift?.x,
        },
        {
          wrapper: "translateY",
          value: transformAnimation?.desktop?.infinite?.shift?.y,
        },
        {
          wrapper: "translateZ",
          value: transformAnimation?.desktop?.infinite?.shift?.z,
        },
        {
          wrapper: "rotateX",
          value: transformAnimation?.desktop?.infinite?.rotate?.x,
        },
        {
          wrapper: "rotateY",
          value: transformAnimation?.desktop?.infinite?.rotate?.y,
        },
        {
          wrapper: "rotateZ",
          value: transformAnimation?.desktop?.infinite?.rotate?.z,
        },
        {
          wrapper: "skewX",
          value: transformAnimation?.desktop?.infinite?.skew?.x,
        },
        {
          wrapper: "skewY",
          value: transformAnimation?.desktop?.infinite?.skew?.y,
        },
        {
          wrapper: "scale",
          value: transformAnimation?.desktop?.infinite?.scale,
        },
      ])}
      ${GrigoraCSSRenderer.opacity(
        transformAnimation?.desktop?.infinite?.opacity,
      )}
    }
  }

  ${
    transformAnimation?.desktop?.infinite?.parent
      ? `${transformAnimation?.desktop?.infinite?.parent} .transform-${id}`
      : `.transform-${id}`
  } {
    ${GrigoraCSSRenderer.animation(
      `transform-${id}`,
      transformAnimation?.desktop?.infinite?.duration
        ? transformAnimation?.desktop?.infinite?.duration + "s"
        : undefined,
      "infinite",
      transformAnimation?.desktop?.infinite?.delay
        ? transformAnimation?.desktop?.infinite?.delay + "s"
        : undefined,
      transformAnimation?.desktop?.infinite?.direction,
    )}
    ${GrigoraCSSRenderer.animationTimingFunction(
      transformAnimation?.desktop?.infinite?.easing,
    )}
    ${
      transformAnimation?.desktop?.infinite?.parent
        ? GrigoraCSSRenderer.animationPlayState("paused")
        : ""
    }
  }

  ${
    transformAnimation?.desktop?.infinite?.parent
      ? `${
          transformAnimation?.desktop?.infinite?.parent
        }:hover .transform-${id} {
      ${
        transformAnimation?.desktop?.infinite?.parent
          ? GrigoraCSSRenderer.animationPlayState("running")
          : ""
      }
    }`
      : ``
  } 
  `;
}

const supportSchema = {
  id: "grigora/css-transform-animations",
  name: "CSS Transform Animations",
  component: CSSTransformAnimations,
  transformProps: transformProps,
  dynamicCSS: dynamicCSS,
};

export default supportSchema;
